<template>
  <q-page class="docs-page">
    <KdsNav
      :logo-label="t('app.name')"
      :links="navLinks"
      :cta-secondary="t('landing.nav.logIn')"
      @cta-secondary-click="goAuth"
    />

    <div class="docs-page__inner">
      <!-- Hero -->
      <header class="docs-page__hero">
        <KdsEyebrow>{{ t('docs.eyebrow') }}</KdsEyebrow>
        <h1 class="docs-page__title">{{ t('docs.title') }}</h1>
        <p class="docs-page__subtitle">{{ t('docs.subtitle') }}</p>
      </header>

      <LegalDocsLayout :anchors="docsAnchors">
        <!-- Overview -->
        <section id="overview" class="docs-page__section" data-reveal>
          <KdsEyebrow>{{ t('docs.overview.eyebrow') }}</KdsEyebrow>
          <h2 class="docs-page__section-title">{{ t('docs.overview.title') }}</h2>
          <p class="docs-page__section-body">{{ t('docs.overview.body') }}</p>
          <div class="docs-page__steps">
            <KdsStepCard
              index="01"
              :tag="t('docs.overview.step1Tag')"
              :title="t('docs.overview.step1Title')"
              :body="t('docs.overview.step1Body')"
            />
            <KdsStepCard
              index="02"
              :tag="t('docs.overview.step2Tag')"
              :title="t('docs.overview.step2Title')"
              :body="t('docs.overview.step2Body')"
            />
            <KdsStepCard
              index="03"
              :tag="t('docs.overview.step3Tag')"
              :title="t('docs.overview.step3Title')"
              :body="t('docs.overview.step3Body')"
            />
          </div>
          <DocsVisualPlaceholder section="overview" :alt="t('docs.visuals.overview')" />
        </section>

        <!-- Create persona -->
        <section id="create-persona" class="docs-page__section docs-page__section--bordered" data-reveal>
          <KdsEyebrow>{{ t('docs.createPersona.eyebrow') }}</KdsEyebrow>
          <h2 class="docs-page__section-title">
            <KdsIcon name="users-round" :size="24" aria-hidden="true" class="docs-page__section-icon" />
            {{ t('docs.createPersona.title') }}
          </h2>
          <p class="docs-page__section-body">{{ t('docs.createPersona.body') }}</p>
          <div class="docs-page__steps">
            <KdsStepCard
              index="01"
              :tag="t('docs.createPersona.step1Tag')"
              :title="t('docs.createPersona.step1Title')"
              :body="t('docs.createPersona.step1Body')"
            />
            <KdsStepCard
              index="02"
              :tag="t('docs.createPersona.step2Tag')"
              :title="t('docs.createPersona.step2Title')"
              :body="t('docs.createPersona.step2Body')"
            />
            <KdsStepCard
              index="03"
              :tag="t('docs.createPersona.step3Tag')"
              :title="t('docs.createPersona.step3Title')"
              :body="t('docs.createPersona.step3Body')"
            />
            <KdsStepCard
              index="04"
              :tag="t('docs.createPersona.step4Tag')"
              :title="t('docs.createPersona.step4Title')"
              :body="t('docs.createPersona.step4Body')"
            />
          </div>
          <DocsVisualPlaceholder section="create-persona" :alt="t('docs.visuals.createPersona')" />
        </section>

        <!-- Create segment -->
        <section id="create-segment" class="docs-page__section docs-page__section--bordered" data-reveal>
          <KdsEyebrow>{{ t('docs.createSegment.eyebrow') }}</KdsEyebrow>
          <h2 class="docs-page__section-title">
            <KdsIcon name="layers" :size="24" aria-hidden="true" class="docs-page__section-icon" />
            {{ t('docs.createSegment.title') }}
          </h2>
          <p class="docs-page__section-body">{{ t('docs.createSegment.body') }}</p>
          <div class="docs-page__steps">
            <KdsStepCard
              index="01"
              :tag="t('docs.createSegment.step1Tag')"
              :title="t('docs.createSegment.step1Title')"
              :body="t('docs.createSegment.step1Body')"
            />
            <KdsStepCard
              index="02"
              :tag="t('docs.createSegment.step2Tag')"
              :title="t('docs.createSegment.step2Title')"
              :body="t('docs.createSegment.step2Body')"
            />
            <KdsStepCard
              index="03"
              :tag="t('docs.createSegment.step3Tag')"
              :title="t('docs.createSegment.step3Title')"
              :body="t('docs.createSegment.step3Body')"
            />
          </div>
          <DocsVisualPlaceholder section="create-segment" :alt="t('docs.visuals.createSegment')" />
        </section>

        <!-- Start debate -->
        <section id="start-debate" class="docs-page__section docs-page__section--bordered" data-reveal>
          <KdsEyebrow>{{ t('docs.startDebate.eyebrow') }}</KdsEyebrow>
          <h2 class="docs-page__section-title">
            <KdsIcon name="message-square-quote" :size="24" aria-hidden="true" class="docs-page__section-icon" />
            {{ t('docs.startDebate.title') }}
          </h2>
          <p class="docs-page__section-body">{{ t('docs.startDebate.body') }}</p>
          <div class="docs-page__steps">
            <KdsStepCard
              index="01"
              :tag="t('docs.startDebate.step1Tag')"
              :title="t('docs.startDebate.step1Title')"
              :body="t('docs.startDebate.step1Body')"
            />
            <KdsStepCard
              index="02"
              :tag="t('docs.startDebate.step2Tag')"
              :title="t('docs.startDebate.step2Title')"
              :body="t('docs.startDebate.step2Body')"
            />
            <KdsStepCard
              index="03"
              :tag="t('docs.startDebate.step3Tag')"
              :title="t('docs.startDebate.step3Title')"
              :body="t('docs.startDebate.step3Body')"
            />
            <KdsStepCard
              index="04"
              :tag="t('docs.startDebate.step4Tag')"
              :title="t('docs.startDebate.step4Title')"
              :body="t('docs.startDebate.step4Body')"
            />
          </div>
          <DocsVisualPlaceholder section="start-debate" :alt="t('docs.visuals.startDebate')" />
        </section>

        <!-- Read verdict -->
        <section id="read-verdict" class="docs-page__section docs-page__section--bordered" data-reveal>
          <KdsEyebrow>{{ t('docs.readVerdict.eyebrow') }}</KdsEyebrow>
          <h2 class="docs-page__section-title">
            <KdsIcon name="scale" :size="24" aria-hidden="true" class="docs-page__section-icon" />
            {{ t('docs.readVerdict.title') }}
          </h2>
          <p class="docs-page__section-body">{{ t('docs.readVerdict.body') }}</p>
          <div class="docs-page__steps">
            <KdsStepCard
              index="01"
              :tag="t('docs.readVerdict.step1Tag')"
              :title="t('docs.readVerdict.step1Title')"
              :body="t('docs.readVerdict.step1Body')"
            />
            <KdsStepCard
              index="02"
              :tag="t('docs.readVerdict.step2Tag')"
              :title="t('docs.readVerdict.step2Title')"
              :body="t('docs.readVerdict.step2Body')"
            />
            <KdsStepCard
              index="03"
              :tag="t('docs.readVerdict.step3Tag')"
              :title="t('docs.readVerdict.step3Title')"
              :body="t('docs.readVerdict.step3Body')"
            />
          </div>
          <DocsVisualPlaceholder section="read-verdict" :alt="t('docs.visuals.readVerdict')" />
        </section>
      </LegalDocsLayout>
    </div>

    <KdsFooter
      :brand="t('app.name')"
      :status-label="t('landing.footer.status')"
      :status-to="'/status'"
      :columns="footerColumns"
    />
  </q-page>
</template>

<script setup lang="ts">
import { computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import KdsNav from '@/kds/components/KdsNav.vue';
import KdsFooter from '@/kds/components/KdsFooter.vue';
import KdsEyebrow from '@/kds/primitives/KdsEyebrow.vue';
import KdsStepCard from '@/kds/components/KdsStepCard.vue';
import KdsIcon from '@/kds/primitives/KdsIcon.vue';
import LegalDocsLayout from '@/components/static/LegalDocsLayout.vue';
import DocsVisualPlaceholder from '@/components/docs/DocsVisualPlaceholder.vue';
import { useScrollToAnchor } from '@/composables/useScrollToAnchor';
import { useRevealOnScroll } from '@/composables/useRevealOnScroll';
import { useConsent } from '@/composables/useConsent';
import { useSeoMeta } from '@/composables/useSeoMeta';

const { t } = useI18n();

useSeoMeta('docs');
useRevealOnScroll();
const router = useRouter();
const route = useRoute();
const { scrollToHash } = useScrollToAnchor();
const { openPreferences } = useConsent();

const navLinks = computed(() => [
  { label: t('landing.nav.product'), href: '/#how' },
  { label: t('landing.nav.protocols'), href: '/#protocols' },
  { label: t('landing.nav.pricing'), href: '/#pricing' },
  { label: t('landing.nav.docs'), href: '/docs' },
  { label: t('landing.nav.changelog'), href: '/changelogs' }
]);

const docsAnchors = computed(() => [
  { label: t('docs.nav.overview'), anchor: 'overview' },
  { label: t('docs.nav.createPersona'), anchor: 'create-persona' },
  { label: t('docs.nav.createSegment'), anchor: 'create-segment' },
  { label: t('docs.nav.startDebate'), anchor: 'start-debate' },
  { label: t('docs.nav.readVerdict'), anchor: 'read-verdict' }
]);

const footerColumns = computed(() => [
  {
    title: t('landing.footer.productTitle'),
    links: [
      { label: t('landing.footer.linkOverview'), href: '/#how' },
      { label: t('landing.footer.linkProtocols'), href: '/#protocols' },
      { label: t('landing.footer.linkPricing'), href: '/#pricing' }
    ]
  },
  {
    title: t('landing.footer.resourcesTitle'),
    links: [
      { label: t('landing.footer.linkDocs'), to: '/docs' },
      { label: t('landing.footer.linkChangelog'), to: '/changelogs' },
      { label: t('landing.footer.linkStatus'), to: '/status' }
    ]
  },
  {
    title: t('landing.footer.companyTitle'),
    links: [
      { label: t('landing.footer.linkAbout'), to: '/about' },
      { label: t('landing.footer.linkContact'), to: { path: '/about', hash: '#contact' } }
    ]
  },
  {
    title: t('landing.footer.legalTitle'),
    links: [
      { label: t('landing.footer.linkPrivacy'), to: '/privacy' },
      { label: t('landing.footer.linkTerms'), to: '/terms' },
      { label: t('landing.footer.linkCookies'), to: '/cookies' },
      { label: t('landing.footer.linkLegalNotice'), to: '/legal-notice' },
      { label: t('landing.footer.manageCookies'), action: openPreferences }
    ]
  }
]);

const goAuth = (): void => {
  void router.push({ name: 'auth' });
};

onMounted(async () => {
  if (route.hash) {
    await nextTick();
    scrollToHash(route.hash);
  }
});
</script>

<style scoped lang="scss">
.docs-page {
  background: var(--paper);
  color: var(--ink);

  &__inner {
    max-width: 1280px;
    margin: 0 auto;
    padding: var(--space-11) var(--space-7);
  }

  &__hero {
    margin-bottom: var(--space-9);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  &__title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-h1);
    line-height: 1.18;
    letter-spacing: -0.015em;
    color: var(--ink);
  }

  &__subtitle {
    margin: 0;
    font-size: var(--fs-body);
    color: var(--stone);
  }

  &__section {
    padding-top: var(--space-9);
    scroll-margin-top: calc(var(--nav-height, 56px) + var(--space-2));
    display: flex;
    flex-direction: column;
    gap: var(--space-5);

    &--bordered {
      border-top: var(--border);
    }
  }

  &__section-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-h2);
    line-height: 1.22;
    letter-spacing: -0.012em;
    color: var(--ink);
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  &__section-icon {
    color: var(--stone);
    flex-shrink: 0;
  }

  &__section-body {
    margin: 0;
    font-size: var(--fs-body);
    line-height: 1.65;
    color: var(--stone);
  }

  &__steps {
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--space-4);

    @media (min-width: 640px) {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}
</style>
